"use client";

import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { FileTextIcon, PlusIcon, PencilIcon, Trash2Icon } from "lucide-react";

import type { DocumentSignataire } from "@/prisma/generated/client";
import {
  invalidateDocumentSignatairesQuery,
  useDocumentSignatairesQuery,
} from "@/lib/queries";
import { deleteDocumentSignataire } from "@/app/lib/actions/document-signataires";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import DocumentSignataireForm from "./document-signataire-form";

function AdministrationDocumentSignataires() {
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useDocumentSignatairesQuery({
    sort: { createdAt: "desc" },
  });

  const [formOpen, setFormOpen] = useState(false);
  const [editingSignataire, setEditingSignataire] =
    useState<DocumentSignataire | null>(null);
  const [deletingSignataire, setDeletingSignataire] =
    useState<DocumentSignataire | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const refresh = () => invalidateDocumentSignatairesQuery(queryClient);

  const openCreate = () => {
    setEditingSignataire(null);
    setFormOpen(true);
  };

  const openEdit = (signataire: DocumentSignataire) => {
    setEditingSignataire(signataire);
    setFormOpen(true);
  };

  const handleDelete = async () => {
    if (!deletingSignataire) return;
    setDeleteLoading(true);
    await deleteDocumentSignataire({ id: deletingSignataire.id });
    setDeleteLoading(false);
    setDeletingSignataire(null);
    refresh();
  };

  const signataires = data?.documentSignataires ?? [];
  const total = data?.total ?? 0;

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileTextIcon className="text-primary size-5" />
                Signataires documents{" "}
                {!isLoading ? (
                  <span className="text-muted-foreground text-sm font-normal">
                    ({total} entrées)
                  </span>
                ) : null}
              </CardTitle>
              <p className="text-muted-foreground mt-1 text-sm">
                Paramétrage des signataires des documents officiels avec
                historisation et versionnement.
              </p>
            </div>
            <Button size="sm" onClick={openCreate}>
              <PlusIcon className="mr-1.5 size-4" />
              Nouvelle règle
            </Button>
          </div>
        </CardHeader>

        <CardContent className="p-0">
          {isError ? (
            <p className="px-6 py-4 text-sm text-red-600">
              Erreur lors du chargement des signataires documents.
            </p>
          ) : null}

          {isLoading ? (
            <div className="text-muted-foreground flex items-center justify-center py-12 text-sm">
              Chargement…
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-24">Document</TableHead>
                  <TableHead className="w-56">Identifiant</TableHead>
                  <TableHead>Prénom signataire</TableHead>
                  <TableHead>Nom signataire</TableHead>
                  <TableHead className="w-20" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {signataires.map((signataire) => (
                  <TableRow key={signataire.id}>
                    <TableCell className="font-mono">
                      {signataire.documentCode}
                    </TableCell>
                    <TableCell className="font-mono">
                      {signataire.signerIdentifier}
                    </TableCell>
                    <TableCell>{signataire.firstName}</TableCell>
                    <TableCell>{signataire.lastName}</TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-muted-foreground hover:text-primary size-7"
                          onClick={() => openEdit(signataire)}
                        >
                          <PencilIcon className="size-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-muted-foreground hover:text-destructive size-7"
                          onClick={() => setDeletingSignataire(signataire)}
                        >
                          <Trash2Icon className="size-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {signataires.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      className="text-muted-foreground py-6 text-center"
                    >
                      Aucune règle active.
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          )}

          {!isLoading && signataires.length > 0 ? (
            <div className="text-muted-foreground border-t px-6 py-3 text-xs">
              {total} règles actives · Versionnement actif
            </div>
          ) : null}
        </CardContent>
      </Card>

      <DocumentSignataireForm
        key={editingSignataire?.id ?? "new"}
        signataire={editingSignataire}
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSuccess={refresh}
      />

      <AlertDialog
        open={!!deletingSignataire}
        onOpenChange={(isOpen) => !isOpen && setDeletingSignataire(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer la règle ?</AlertDialogTitle>
            <AlertDialogDescription>
              La règle sera désactivée (soft delete) et historisée via le
              versionnement.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleteLoading}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteLoading ? "Suppression…" : "Supprimer"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export { AdministrationDocumentSignataires };
